import { useEffect, useRef, useState } from 'react';
import type { DataTableViewData } from '@shared/types/views';
import { DataTableRenderer, formatBusinessTableValue } from '@/components/execution/renderers/DataTableRenderer';
import { useWorkActions } from '@/components/work/WorkActionsContext';
import { ToneDot } from './atoms/ToneDot';
import { RowDetail, RowNav } from './table/RowDetail';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { TableToolbar } from './table/TableToolbar';
import { applyTableQuery, INTERACTIVE_FROM_ROWS, quickFilterColumn, type TableQuery } from './table/table-interactions';

interface DataTableViewProps {
  hideTitle?: boolean;
  data: DataTableViewData;
}

const toDisplayText = (value: unknown, columnKey = ''): string => {
  value = formatBusinessTableValue(value, columnKey);
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.trim().length > 0 ? value : '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/**
 * Drilldown: clicking a row opens its fiche in a drawer beside the table, with
 * the row's full values (the table truncates long cells); closing it brings the
 * focus back to that row. Pure arrangement of the row's own data — no content
 * is fabricated client-side.
 */
export const DataTableView = ({ data, hideTitle }: DataTableViewProps) => {
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Row to focus again when the table comes back.
  const returnRowId = useRef<string | null>(null);
  // Sort, search and quick filter: arrangement of the rows received, never stored.
  const [query, setQuery] = useState<TableQuery>({ sort: null, search: '', filter: null });
  const quickFilter = quickFilterColumn(data);
  const visibleRows = applyTableQuery(data.rows, query);
  const toggleSort = (key: string) => setQuery((current) => ({
    ...current,
    sort: current.sort?.key !== key ? { key, direction: 'asc' } : current.sort.direction === 'asc' ? { key, direction: 'desc' } : null,
  }));
  // Row whose CRM preparation was started from the table's action column.
  const [crmStartRowId, setCrmStartRowId] = useState<string | null>(null);
  // A row of a live run result can be added to the CRM; historical or
  // run-less surfaces (dashboards, previews) stay read-only.
  const work = useWorkActions();
  const sourceRunId = work?.historical ? null : work?.sourceRunId ?? null;

  // A background refresh re-creates `data`; keep the open fiche (and any
  // action running in it) unless its row is gone.
  const rowIdsKey = data.rows.map((row) => row.id).join('\u0000');
  useEffect(() => {
    setSelectedRowId((current) => (current && rowIdsKey.split('\u0000').includes(current) ? current : null));
  }, [rowIdsKey]);

  const rows = visibleRows.map((row) => row.cells.reduce<Record<string, string | number | boolean | null>>((accumulator, cell) => {
    accumulator[cell.key] = cell.value;
    return accumulator;
  }, { id: row.id }));

  const tones = new Map(data.rows.flatMap((row) => row.cells.filter((cell) => cell.tone).map((cell) => [`${row.id}\u0000${cell.key}`, cell.tone!] as const)));

  const columnLabels = data.columns.reduce<Record<string, string>>((accumulator, column) => {
    accumulator[column.key] = column.label;
    return accumulator;
  }, {});

  const selectedRow = data.rows.find((row) => row.id === selectedRowId) ?? null;
  const selectedIndex = selectedRow ? visibleRows.findIndex((row) => row.id === selectedRow.id) : -1;
  const selectedPosition = selectedIndex >= 0 ? { index: selectedIndex, total: visibleRows.length } : null;
  const openRow = (rowId: string | null) => { if (rowId) setSelectedRowId(rowId); };
  const moveRow = (step: -1 | 1) => openRow(visibleRows[selectedIndex + step]?.id ?? null);
  const backToList = () => {
    returnRowId.current = selectedRowId;
    setSelectedRowId(null);
  };

  // Back on the table: focus the opened row (table or phone card, whichever shows).
  useEffect(() => {
    const rowId = returnRowId.current;
    if (selectedRowId || !rowId) return;
    returnRowId.current = null;
    const matches = [...(containerRef.current?.querySelectorAll<HTMLElement>('[data-row-id]') ?? [])]
      .filter((element) => element.dataset.rowId === rowId);
    const target = matches.find((element) => element.getClientRects().length > 0) ?? matches[0];
    target?.focus({ preventScroll: true });
    target?.scrollIntoView?.({ block: 'center' });
  }, [selectedRowId]);

  return (
    <div ref={containerRef} className="space-y-3">
      {!hideTitle && data.title && (
        <p className="text-sm font-medium text-domino-ink">{data.title}</p>
      )}
      {data.source_note && <p className="text-xs leading-5 text-domino-inkSoft">{data.source_note}</p>}
      {rows.length === 0 && data.empty_message ? (
        <div className="text-sm text-domino-inkSoft">
          {data.empty_message}
        </div>
      ) : (
        <>
          {data.rows.length >= INTERACTIVE_FROM_ROWS && (
            <TableToolbar query={query} onChange={setQuery} filter={quickFilter} shown={visibleRows.length} total={data.rows.length} columns={data.columns} />
          )}
          {visibleRows.length === 0 && (
            <p className="text-sm text-domino-inkSoft">Aucune ligne ne correspond.</p>
          )}
          {/* On a phone a wide table shows two columns: each row becomes a card. */}
          <ul className="divide-y divide-domino-cardBorder border-y border-domino-cardBorder sm:hidden" aria-label={data.title ?? 'Résultats'}>
            {visibleRows.map((row) => {
              const [first, ...rest] = row.cells;
              return (
                <li key={row.id}>
                  <button type="button" data-row-id={row.id} onClick={() => openRow(row.id)}
                    className="w-full py-3 text-left">
                    <p className="text-sm font-semibold text-domino-ink">{toDisplayText(first?.value, first?.key)}</p>
                    <dl className="mt-1 space-y-0.5">
                      {rest.filter((cell) => toDisplayText(cell.value, cell.key) !== '—').map((cell) => (
                        <div key={cell.key} className="flex gap-1.5 text-xs leading-5">
                          <dt className="shrink-0 text-domino-inkSoft">{columnLabels[cell.key] ?? cell.key}{'\u00a0:'}</dt>
                          <dd className="flex min-w-0 items-center gap-1.5 text-domino-ink">
                            {cell.tone && <ToneDot tone={cell.tone} />}
                            <span className="line-clamp-1">{toDisplayText(cell.value, cell.key)}</span>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </button>
                </li>
              );
            })}
            <li className="py-2 text-xs text-domino-inkSoft">{visibleRows.length} résultat{visibleRows.length > 1 ? 's' : ''}</li>
          </ul>
          <div className={visibleRows.length === 0 ? 'hidden' : 'hidden sm:block'}>
          <DataTableRenderer
            sort={query.sort}
            onSort={toggleSort}
            cellTone={tones.size > 0 ? (row, column) => tones.get(`${String(row.id)}\u0000${column}`) : undefined}
            rows={rows}
            columns={data.columns.map((column) => column.key)}
            columnLabels={columnLabels}
            preserveDeclaredColumns
            onRowClick={(row) => openRow(typeof row.id === 'string' ? row.id : null)}
            rowAction={sourceRunId ? {
              label: 'Ajouter au CRM',
              onClick: (row) => {
                const rowId = typeof row.id === 'string' ? row.id : null;
                setSelectedRowId(rowId);
                setCrmStartRowId(rowId);
              },
            } : undefined}
          />
          </div>
        </>
      )}
      {selectedRow && (
        <SideDrawer label="Fiche détaillée" title={data.title} onClose={backToList}
          actions={<RowNav position={selectedPosition} onMove={moveRow} />}>
          <RowDetail
            row={selectedRow}
            columnLabels={columnLabels}
            statusKey={quickFilter?.key ?? null}
            position={selectedPosition}
            onBack={backToList}
            onMove={moveRow}
            crm={sourceRunId ? { sourceRunId, autoStart: crmStartRowId === selectedRow.id, onStarted: () => setCrmStartRowId(null) } : null}
          />
        </SideDrawer>
      )}
    </div>
  );
};
